/**
 * Utility functions for optimizing Cloudinary image URLs to improve page load speed and Lighthouse scores.
 */

/** Max transform width — high enough for full-bleed retina desktops, still capped vs raw 4K uploads. */
const MAX_CLOUDINARY_WIDTH = 2560;

export const optimizeCloudinaryUrl = (url, width, aspectRatio, quality = 'good') => {
  if (!url || typeof url !== 'string' || !url.includes('res.cloudinary.com')) {
    return url;
  }
  const uploadToken = '/image/upload';
  if (!url.includes(uploadToken)) return url;

  const parts = url.split(uploadToken);
  const baseUrl = parts[0];
  let rest = parts[1];

  // Strip any existing transformation parameters before /v<digits>/ if present
  const vMatch = rest.match(/(\/v\d+\/.*)$/);
  if (vMatch) {
    rest = vMatch[1];
  } else {
    // If no /v12345/ version prefix, take the last path segment (the image file)
    const lastSlash = rest.lastIndexOf('/');
    if (lastSlash > 0) {
      rest = rest.substring(lastSlash);
    }
  }

  const cappedWidth = width ? Math.min(width, MAX_CLOUDINARY_WIDTH) : 1200;
  const arTransform = aspectRatio ? `,ar_${aspectRatio},c_fill,g_auto` : ',c_limit';
  const transformation = `f_auto,q_auto:${quality},w_${cappedWidth}${arTransform}`;
  return `${baseUrl}${uploadToken}/${transformation}${rest}`;
};

/** Default srcset for cards / smaller surfaces. */
export const DEFAULT_SRCSET_WIDTHS = [400, 600, 800, 1000, 1200];

/** Full-bleed desktop hero / bottom image (covers 1x–2x up to ~1280–1920 CSS px). */
export const DESKTOP_HERO_SRCSET_WIDTHS = [960, 1280, 1600, 1920, 2560];

/** Tablet portrait crops including 2x denser displays. */
export const TABLET_SRCSET_WIDTHS = [600, 800, 1000, 1240, 1600];

/** Mobile portrait crops including 3x denser displays. */
export const MOBILE_SRCSET_WIDTHS = [400, 600, 800, 1000, 1200];

export const getCloudinarySrcSet = (
  url,
  aspectRatio,
  widths = DEFAULT_SRCSET_WIDTHS,
  quality = 'good'
) => {
  if (!url || typeof url !== 'string' || !url.includes('res.cloudinary.com')) {
    return null;
  }
  return widths
    .map(w => `${optimizeCloudinaryUrl(url, w, aspectRatio, quality)} ${w}w`)
    .join(', ');
};
